import calibratePhoto from '../../../elements/calibrate-photo/calibrate-photo.js';
import { getStickAlignmentData } from '../../photo-data/StickAlign.js';
import { calcPhotoClearances } from '../../photo-data/MRClearanceCalculations.js';
import { getClearanceNodeData } from '../../photo-data/MRClearances.js';
import {
  applyUpdateToObject,
  getPhotoAssociatedItemRecord,
  photoHasPhotoFirstData,
  rebaseUpdatePaths,
  updateObjectFromDiff
} from '../KatapultJobUtilities.js';
import { composeItemAttributesUpdate } from '../KatapultJobUpdateComposers.js';
import { CalcPhotoDistanceRatios } from '../../ApplyEffectiveMoves.js';
import { GetMainPhoto } from '../../GetMainPhoto.js';
import { serverTimestamp } from 'firebase/database';
import { Path } from '../../Path.js';
import { ParseHeight } from '../../FormatHeight.js';
import { Round } from '../../Round.js';

/** @import { StickAlignmentPoint } from '../../photo-data/StickAlign.js' */
/** @import { PixelSelection } from '../KatapultJobTypes.d.ts' */
/** @import { PhotoElementCreateParams, PhotoElementUpdateParams } from '../KatapultJobUtilities.js' */

// TODO (2024-09-10): Check for MR models (if they don't exist, we can skip the MR state updates)

/**
 * Composes an update object for an anchor marker.
 * @param {string} photoId - The id of the photo that the anchor marker belongs to
 * @param {string} anchorMarkerId - The id of the anchor marker to update
 * @param {{ height: string, pixel_selection: PixelSelection }|null} anchorMarkerData - The new anchor marker data (or null if the anchor
 * marker should be deleted)
 * @param {object} supplementalData - Supplemental data
 * @param {object} supplementalData.photo - The photo object
 * @param {object} supplementalData.model - The job's associated model
 * @param {string} supplementalData.itemType - The type of the item that the photo belongs to
 * @param {object} supplementalData.item - The item that the photo belongs to
 * @param {string} supplementalData.itemId - The id of the item that the photo belongs to
 * @param {object} supplementalData.jobTraces - The job's traces
 * @param {object} supplementalData.jobNodes - The job's nodes
 * @param {object} supplementalData.jobConnections - The job's connections
 * @param {object} supplementalData.jobStyles - The job's styles
 * @param {string} [supplementalData.userUid] - The uid of the user that is updating the anchor marker (should be included if possible)
 * @param {object} [options]
 * @param {boolean} [options.forciblyDeleteStickAlign] - Whether to forcibly delete the stick align object from the photo, requiring the
 * photo to be viewed in Katapult Pro for re-calibration.
 * @returns {object} The update object (based at the job)
 */
export function composeAnchorMarkerUpdate(photoId, anchorMarkerId, anchorMarkerData, supplementalData, options = {}) {
  const { forciblyDeleteStickAlign = false } = options;

  // Expand the supplemental data
  const { photo, model, userUid } = supplementalData;

  // Extract the anchor marker data (for use in the update later)
  const { height = null, pixel_selection: pixelSelection = null } = anchorMarkerData ?? {};

  // Clone the photo object to avoid mutating the original
  const modifiedCloneOfPhoto = structuredClone(photo);

  // Determine if the anchor marker is being deleted
  const isAnchorMarkerDeletion = anchorMarkerData == null;
  const existingAnchorCalibration = modifiedCloneOfPhoto.photofirst_data?.anchor_calibration ?? {};
  // Get an updated anchor calibration object depending on whether the anchor marker is being deleted or not
  const updatedAnchorCalibration = isAnchorMarkerDeletion
    ? Object.fromEntries(Object.entries(existingAnchorCalibration).filter(([key]) => key != anchorMarkerId))
    : { ...modifiedCloneOfPhoto.photofirst_data?.anchor_calibration, [anchorMarkerId]: anchorMarkerData };

  // Update the clone of the photo with the new anchor marker data
  Path.set(modifiedCloneOfPhoto, `photofirst_data.anchor_calibration`, updatedAnchorCalibration);

  // Get the stick alignment data and points
  const { stickAlignIsPossible, stickAlignData, stickAlignPoints } = getStickAlignmentData(modifiedCloneOfPhoto);

  // Get the stick alignment update
  const photoUpdateToSetOrRemoveStickAlignment =
    stickAlignIsPossible && Array.isArray(stickAlignPoints) && !forciblyDeleteStickAlign
      ? composeStickAlignUpdate(stickAlignData, stickAlignPoints, { photo: modifiedCloneOfPhoto, model })
      : composeStickAlignDeleteUpdate({ photo: modifiedCloneOfPhoto, model });
  // Apply the stick alignment update to the clone of the photo
  applyUpdateToObject(modifiedCloneOfPhoto, photoUpdateToSetOrRemoveStickAlignment);

  // Determine if the photo summary should be updated
  const updatedPhotoHasPhotoFirstData = photoHasPhotoFirstData(modifiedCloneOfPhoto);
  const updatePhotoSummary = photoHasPhotoFirstData(photo) !== updatedPhotoHasPhotoFirstData;

  const cleanedHeight = height == null ? null : String(height);
  const jobUpdate = {
    // Add or remove stick alignment based on the new anchor marker data (and update all measured heights accordingly)
    ...rebaseUpdatePaths(`photos/${photoId}`, photoUpdateToSetOrRemoveStickAlignment),
    // Update the MR state and ground marker if necessary
    ...composeMRStateAndAutoGroundMarkerUpdate({ ...supplementalData, photoId, photo: modifiedCloneOfPhoto }),
    // Update photo editors list (if a uid was provided)
    ...(userUid && { [`photos/${photoId}/photofirst_data/_editors/${userUid}`]: serverTimestamp() }),
    // TODO (2024-09-03): Increment photo counters?
    ...(updatePhotoSummary && { [`photo_summary/${photoId}/data`]: updatedPhotoHasPhotoFirstData })
  };
  // Update, create, or delete the anchor marker based on pixel selection
  if (!pixelSelection) jobUpdate[`photos/${photoId}/photofirst_data/anchor_calibration/${anchorMarkerId}`] = null;
  else {
    jobUpdate[`photos/${photoId}/photofirst_data/anchor_calibration/${anchorMarkerId}/height`] = cleanedHeight;
    jobUpdate[`photos/${photoId}/photofirst_data/anchor_calibration/${anchorMarkerId}/pixel_selection`] = pixelSelection;
  }

  return jobUpdate;
}

/**
 * Composes an update object for a valid stick alignment.
 * @param {object} stickAlignData - The stick alignment data
 * @param {StickAlignmentPoint[]} stickAlignPoints - The stick alignment points
 * @param {object} supplementalData - Supplemental data
 * @param {object} supplementalData.photo - The photo object
 * @param {object} supplementalData.model - The job's associated model
 * @returns {object} The update object (based at the photo)
 */
export function composeStickAlignUpdate(stickAlignData, stickAlignPoints, supplementalData) {
  // Expand the supplemental data
  const { photo, model } = supplementalData;
  const { input_models: inputModelDefinitions } = model;

  // Extract the anchor calibration markers and other input models from the photo
  const { anchor_calibration: anchorCalibrationMarkers, ...otherInputModels } = photo.photofirst_data ?? {};

  // Filter the input model instances that have pixel selections
  const measuredTopLevelMarkerEntries = getMeasuredTopLevelMarkerEntries(otherInputModels, inputModelDefinitions);

  // Calculate the new measured heights for the input models
  const { width, height } = stickAlignData;
  const measuredHeightUpdatesEntries = measuredTopLevelMarkerEntries.map(([inputModelKeyAndInstanceKey, inputModelInstance]) => {
    const [{ percentX, percentY }] = inputModelInstance.pixel_selection;
    const measuredHeight = calibratePhoto.getInches((width * percentX) / 100, (height * percentY) / 100, stickAlignData);

    return [inputModelKeyAndInstanceKey, measuredHeight];
  });

  // Also get a list of input model instances that have empty manual heights
  const inputModelInstanceEntriesWithEmptyManualHeights = measuredTopLevelMarkerEntries.filter(
    ([, inputModelInstance]) => inputModelInstance.manual_height == ''
  );

  const anchorEntries = Object.entries(anchorCalibrationMarkers ?? {}).map(([anchorId, anchorData]) => ({
    anchorId,
    x: (anchorData.pixel_selection[0].percentX * width) / 100,
    y: (anchorData.pixel_selection[0].percentY * height) / 100
  }));
  const anchorLeastSquaresCoefficients = calibratePhoto.getLeastSquaresLineThroughAnchors(anchorEntries);

  const photoUpdate = {
    ['stick_align']: stickAlignData,
    // Update the anchor calibration markers with the new scores
    ...Object.fromEntries(
      Object.entries(anchorCalibrationMarkers ?? {}).map(([anchorMarkerKey, anchorMarker]) => [
        `photofirst_data/anchor_calibration/${anchorMarkerKey}/_score`,
        stickAlignPoints?.find((point) => point.height == anchorMarker.height)?.score
      ])
    ),
    // Update the anchor calibration markers with the new residuals
    ...Object.fromEntries(
      anchorEntries.map((anchor) => {
        const residual = calibratePhoto.getAnchorResidual(anchor, anchorLeastSquaresCoefficients);
        const residualScore = calibratePhoto.getResidualScore(residual, width);
        return [`photofirst_data/anchor_calibration/${anchor.anchorId}/_residual_score`, residualScore];
      })
    ),
    // Update the input models with the new measured heights
    ...Object.fromEntries(
      measuredHeightUpdatesEntries.map(([inputModelKeyAndInstanceKey, measuredHeight]) => [
        `photofirst_data/${inputModelKeyAndInstanceKey}/_measured_height`,
        measuredHeight
      ])
    ),
    // Clear the manual heights of the input models that have empty manual heights
    ...Object.fromEntries(
      inputModelInstanceEntriesWithEmptyManualHeights.map(([inputModelKeyAndInstanceKey]) => [
        `photofirst_data/${inputModelKeyAndInstanceKey}/manual_height`,
        null
      ])
    )
  };

  return photoUpdate;
}

/**
 * Composes an update object to remove stick alignment from a photo.
 * @param {object} supplementalData - Supplemental data
 * @param {object} supplementalData.photo - The photo object
 * @param {object} supplementalData.model - The job's associated model
 * @returns {object} The update object (based at the photo)
 */
export function composeStickAlignDeleteUpdate(supplementalData) {
  // Expand the supplemental data
  const { photo, model } = supplementalData;
  const { input_models: inputModelDefinitions } = model;

  // Extract the anchor calibration markers and other input models from the photo
  const { anchor_calibration: anchorCalibrationMarkers, ...otherInputModels } = photo.photofirst_data ?? {};

  // Get the entries list of input model instances with their combined model and instance keys
  const measuredTopLevelMarkerEntries = getMeasuredTopLevelMarkerEntries(otherInputModels, inputModelDefinitions);

  // Also get a list of input model instances that have null manual heights
  const inputModelInstanceEntriesWithoutManualHeights = measuredTopLevelMarkerEntries.filter(
    ([, inputModelInstance]) => inputModelInstance.manual_height == null
  );

  const photoUpdate = {
    ['stick_align']: null,
    // Remove the scores from the anchor calibration markers
    ...Object.fromEntries(
      Object.entries(anchorCalibrationMarkers ?? {}).map(([anchorMarkerKey]) => [
        `photofirst_data/anchor_calibration/${anchorMarkerKey}/_score`,
        null
      ])
    ),
    // Clear the measured heights of the input models
    ...Object.fromEntries(
      measuredTopLevelMarkerEntries.map(([inputModelKeyAndInstanceKey]) => [
        `photofirst_data/${inputModelKeyAndInstanceKey}/_measured_height`,
        null
      ])
    ),
    // Add the manual heights back to the input models that don't have manual heights
    ...Object.fromEntries(
      inputModelInstanceEntriesWithoutManualHeights.map(([inputModelKeyAndInstanceKey]) => [
        `photofirst_data/${inputModelKeyAndInstanceKey}/manual_height`,
        ''
      ])
    )
  };

  return photoUpdate;
}

/**
 * Composes an update object for the MR state of an item (and updates the auto-calculated ground marker for the photo as needed).
 * @param {object} supplementalData - Supplemental data
 * @param {string} supplementalData.photoId - The id of the photo that the MR state belongs to
 * @param {object} supplementalData.photo - The photo object
 * @param {object} supplementalData.model - The job's associated model
 * @param {string} supplementalData.itemType - The type of the item that the photo belongs to
 * @param {object} supplementalData.item - The item that the photo belongs to
 * @param {string} supplementalData.itemId - The id of the item that the photo belongs to
 * @param {object} supplementalData.jobStyles - The job's styles
 * @param {object} supplementalData.jobTraces - The job's traces
 * @param {object} supplementalData.jobNodes - The job's nodes
 * @param {object} supplementalData.jobConnections - The job's connections
 * @returns {object} The update object (based at the job)
 */
function composeMRStateAndAutoGroundMarkerUpdate(supplementalData) {
  const { photoId, photo, model, itemType, item, itemId, jobStyles, jobTraces, jobNodes, jobConnections } = supplementalData;
  const { make_ready: modelMakeReady, config: modelConfig } = model;
  const { auto_insert_ground_marker_in_pole_photo: autoInsertGroundMarkerInPolePhoto, dont_overwrite_mr_state: doNotOverwriteMRState } =
    modelConfig?.photofirst ?? {};

  const sectionId = itemType == 'section' ? itemId?.split(':')[1] : undefined;
  const connectionId = itemType == 'section' ? itemId?.split(':')[0] : undefined;

  const effectiveMovesDistanceRatios =
    itemType == 'section' ? CalcPhotoDistanceRatios(connectionId, sectionId, jobNodes, jobConnections) : undefined;

  const nodeData = getClearanceNodeData({
    itemType,
    item,
    itemId: itemType == 'section' ? sectionId : itemId,
    connectionId,
    nodes: jobNodes,
    connections: jobConnections
  });

  const existingGroundMarker = photo?.photofirst_data?.ground_marker?.auto_added ?? {};

  // Calculate the new MR state and ground marker
  const { MRStateHasChanged, newMRStateValue, computedGroundMarker, groundMarkerHasChanged } = calcPhotoClearances(
    item,
    photo,
    photoId,
    itemType,
    { traces: jobTraces, makeReadyRules: modelMakeReady?.make_ready_items, nodeData },
    { autoInsertGroundMarkerInPolePhoto, effectiveMovesDistanceRatios }
  );

  // Only the main photo for an item should drive MR state updates
  const isMainPhoto = photoId === GetMainPhoto(item?.photos);

  // Determine if the MR state and ground marker should be updated
  const updateMRState = MRStateHasChanged && !doNotOverwriteMRState && isMainPhoto;
  const updateGroundMarker = groundMarkerHasChanged && computedGroundMarker != null;

  // Compose the update object
  const jobUpdate = {
    // Update the MR state and ground marker if necessary
    ...(updateMRState &&
      composeItemAttributesUpdate(itemType, itemId, item, { mr_state: { auto_calced: newMRStateValue } }, { jobStyles })),
    ...(updateGroundMarker &&
      rebaseUpdatePaths(
        `photos/${photoId}/photofirst_data/ground_marker/auto_added`,
        updateObjectFromDiff(existingGroundMarker, computedGroundMarker)
      ))
  };

  return jobUpdate;
}

/**
 * Error thrown when photo element data breaks a model rule, such as a point marker missing its pixel selection.
 */
export class PhotoElementValidationError extends Error {
  name = 'PhotoElementValidationError';
}

/**
 * Composes an update object for creating a new photo element.
 * @param {string} photoId - Id of the photo that the new element belongs to
 * @param {PhotoElementCreateParams} newElementData - Data for the new element
 * @param {PhotoElementSupplementalData} supplementalData - Supplemental data
 */
export function composePhotoElementCreate(photoId, newElementData, supplementalData) {
  if (!newElementData.id) throw new Error(`Element id is required for creating a new element`);
  const { photo, userUid } = supplementalData;

  const modifiedCloneOfPhoto = structuredClone(photo);
  const { parent_id: parentIdOrNull = null, element_type: elementType, id: elementId, ...otherNewElementData } = newElementData;
  const allElements = getFlatPhotoElementList(photo.photofirst_data);
  const newElementPath = getNewElementPath(parentIdOrNull, { _context: { type: elementType, id: elementId } }, allElements);
  checkPhotoElementIsValid(elementType, otherNewElementData, supplementalData, parentIdOrNull != null);
  checkPhotoElementNestingIsValid(elementType, parentIdOrNull, allElements, supplementalData);

  // If there is other data to set, set it on the new element
  if (Object.values(otherNewElementData).some((value) => value != null)) {
    // Add the new element as an empty object (for the update to add to). This is a bit hacky because the diff-based update generation will
    // compare the updates made in the other composer with this empty object version, and it conveniently works while being technically
    // incorrect. A code restructure could make this cleaner.
    Path.set(modifiedCloneOfPhoto, `photofirst_data.${newElementPath}`, {});

    return composePhotoElementUpdate(photoId, elementId, otherNewElementData, { ...supplementalData, photo: modifiedCloneOfPhoto });
  }

  // Otherwise, just add the new element with only its placeholder set
  setElementPlaceholder(modifiedCloneOfPhoto, newElementPath);
  removeRedundantPlaceholders(modifiedCloneOfPhoto, newElementPath);
  if (userUid) Path.set(modifiedCloneOfPhoto, `photofirst_data._editors.${userUid}`, serverTimestamp());
  const photoUpdate = updateObjectFromDiff(photo, modifiedCloneOfPhoto);

  return {
    ...rebaseUpdatePaths(`photos/${photoId}`, photoUpdate),
    ...composeMRStateAndAutoGroundMarkerUpdate({ ...supplementalData, photoId, photo: modifiedCloneOfPhoto }),
    ...(userUid != null && { [`photos/${photoId}/photofirst_data/_editors/${userUid}`]: serverTimestamp() })
  };
}

/**
 * Composes an update object for adding or removing a marker from a trace.
 * @param {string} photoId - Id of the photo that the element belongs to
 * @param {string} elementId - Id of the element we are updating
 * @param {?PhotoElementUpdateParams} partialElementDataOrNull - Photo element update parameters, or null to delete the element
 * @param {PhotoElementSupplementalData} supplementalData - Supplemental data
 * @returns {object} Update object (based at the job)
 */
export function composePhotoElementUpdate(photoId, elementId, partialElementDataOrNull, supplementalData) {
  const { photo, jobTraces, userUid } = supplementalData;

  // Get the marker from the photo
  const allElements = getFlatPhotoElementList(photo.photofirst_data);
  const elementWithContext = allElements[elementId];
  const [rootElementType, ...elementPathParts] = elementWithContext._context.path.split('/');
  const elementPath = `${rootElementType}.${elementPathParts.join('.')}`;

  // Clone the photo object and traces to avoid mutating the original
  const modifiedCloneOfPhoto = structuredClone(photo);
  const modifiedCloneOfTraces = structuredClone(jobTraces);

  // Handle element deletion
  if (partialElementDataOrNull == null) {
    // Recursively remove the element and its children from trace items
    setElementAndChildrenTraceItems(photoId, elementPath, elementWithContext, modifiedCloneOfTraces, { remove: true });

    // Delete the element from the photo
    Path.set(modifiedCloneOfPhoto, `photofirst_data.${elementPath}`, null);
    addPlaceholderToEmptiedParent(modifiedCloneOfPhoto, elementPath);
  }
  // Otherwise, update whitelisted properties
  else {
    // Unpack the update parameters
    const {
      trace_id: traceIdOrNull,
      pixel_selection: pixelSelectionOrNull,
      manual_height: manualHeightOrNull,
      attributes,
      parent_id: parentIdOrNull
    } = partialElementDataOrNull;

    const elementDataAfterUpdate = { ...elementWithContext, ...partialElementDataOrNull };
    const isNestedAfterUpdate = parentIdOrNull === undefined ? elementPathParts.length > 1 : parentIdOrNull !== null;
    // Moving to the top level can leave a point element without a pixel selection, so parent changes are validated too
    const isHeightOrParentChanging = [pixelSelectionOrNull, manualHeightOrNull, parentIdOrNull].some((value) => value !== undefined);
    if (isHeightOrParentChanging) {
      checkPhotoElementIsValid(elementWithContext?._context?.type, elementDataAfterUpdate, supplementalData, isNestedAfterUpdate);
    }
    checkPhotoElementNestingIsValid(elementWithContext._context.type, parentIdOrNull, allElements, supplementalData);

    if (traceIdOrNull !== undefined) {
      // If the item is already in a trace, remove it from that trace
      const existingTraceId = elementWithContext._trace;
      if (existingTraceId != null) {
        // Remove the trace item from the existing trace
        const existingTraceItemPath = `${existingTraceId}.${photoId}.${rootElementType}.${elementPathParts.join(':')}`;
        Path.set(modifiedCloneOfTraces, `trace_items.${existingTraceItemPath}`, null);
      }

      // Update the trace id on the marker
      Path.set(modifiedCloneOfPhoto, `photofirst_data.${elementPath}._trace`, traceIdOrNull);

      // If a new trace id was provided, add the trace item to the new trace
      if (traceIdOrNull !== null) {
        const traceItemPath = `${traceIdOrNull}.${photoId}.${rootElementType}.${elementPathParts.join(':')}`;
        Path.set(modifiedCloneOfTraces, `trace_items.${traceItemPath}`, true);
      }
    }

    if (pixelSelectionOrNull !== undefined) {
      // Element must be at root (or about to be moved to root) to set pixel selection
      if (elementPath.split('.').length !== 2 && parentIdOrNull !== null) {
        throw new PhotoElementValidationError(`Pixel selection can only be set on top-level markers`);
      }

      Path.set(modifiedCloneOfPhoto, `photofirst_data.${elementPath}.pixel_selection`, pixelSelectionOrNull);
      const newMeasuredHeight = getMeasuredHeight(modifiedCloneOfPhoto, pixelSelectionOrNull);
      Path.set(modifiedCloneOfPhoto, `photofirst_data.${elementPath}._measured_height`, newMeasuredHeight);

      // Add or remove a "blank" manual height if necessary
      const existingMeasuredHeight = elementWithContext._measured_height;
      const existingManualHeight = elementWithContext.manual_height;
      if (existingMeasuredHeight != null && newMeasuredHeight == null && existingManualHeight == null) {
        Path.set(modifiedCloneOfPhoto, `photofirst_data.${elementPath}.manual_height`, '');
      } else if (existingMeasuredHeight == null && newMeasuredHeight != null && existingManualHeight == '') {
        Path.set(modifiedCloneOfPhoto, `photofirst_data.${elementPath}.manual_height`, null);
      }
    }

    if (manualHeightOrNull !== undefined) {
      Path.set(modifiedCloneOfPhoto, `photofirst_data.${elementPath}.manual_height`, manualHeightOrNull);
      const newManualHeight = manualHeightOrNull === null ? null : Round(ParseHeight(manualHeightOrNull).decimalFeet * 12, 2);
      Path.set(modifiedCloneOfPhoto, `photofirst_data.${elementPath}._manual_height`, newManualHeight);
    }

    // Null and undefined are both ignored for attributes
    if (attributes != null) {
      for (const [attribute, value] of Object.entries(attributes)) {
        // Ignore underscore-prefixed attributes
        if (attribute.startsWith('_')) continue;
        // Error if we are trying to set an attribute handled with a different method
        if (attribute === 'pixel_selection')
          throw new PhotoElementValidationError(`Pixel selection cannot be set as an attribute. Use the pixel_selection property instead.`);
        if (attribute === 'manual_height')
          throw new PhotoElementValidationError(`Manual height cannot be set as an attribute. Use the manual_height property instead.`);

        Path.set(modifiedCloneOfPhoto, `photofirst_data.${elementPath}.${attribute}`, value);
      }
    }

    // Do nesting stuff last so we can use the element data path for the other updates
    if (parentIdOrNull !== undefined) {
      // Start by removing the element and its children from the trace items
      setElementAndChildrenTraceItems(photoId, elementPath, elementWithContext, modifiedCloneOfTraces, { remove: true });
      // Remove the element from the photo
      const currentElementData = Path.get(modifiedCloneOfPhoto, `photofirst_data.${elementPath}`);
      Path.set(modifiedCloneOfPhoto, `photofirst_data.${elementPath}`, null);
      addPlaceholderToEmptiedParent(modifiedCloneOfPhoto, elementPath);

      // Insert the element into the new parent
      const newElementPath = getNewElementPath(parentIdOrNull, elementWithContext, allElements);
      const movedElementData = parentIdOrNull === null ? currentElementData : getNestedElementData(currentElementData);
      Path.set(modifiedCloneOfPhoto, `photofirst_data.${newElementPath}`, movedElementData);
      // Update trace items
      setElementAndChildrenTraceItems(photoId, newElementPath, movedElementData, modifiedCloneOfTraces);
    }

    const finalElementPath =
      parentIdOrNull === undefined ? elementPath : getNewElementPath(parentIdOrNull, elementWithContext, allElements);
    removeRedundantPlaceholders(modifiedCloneOfPhoto, finalElementPath);
    // An element with no data of its own (such as one created with empty attributes) needs a placeholder so the database keeps it
    const finalElementData = Path.get(modifiedCloneOfPhoto, `photofirst_data.${finalElementPath}`);
    if (!hasOwnElementData(finalElementData)) setElementPlaceholder(modifiedCloneOfPhoto, finalElementPath);
  }

  // Update the photo editors object (if a uid was provided)
  if (userUid) Path.set(modifiedCloneOfPhoto, `photofirst_data._editors.${userUid}`, serverTimestamp());

  const photoUpdate = updateObjectFromDiff(photo, modifiedCloneOfPhoto);
  const traceUpdate = updateObjectFromDiff(jobTraces, modifiedCloneOfTraces);

  // Compose the update object
  const jobUpdate = {
    ...rebaseUpdatePaths(`photos/${photoId}`, photoUpdate),
    ...rebaseUpdatePaths(`traces`, traceUpdate),
    // Update the MR state and ground marker if necessary
    ...composeMRStateAndAutoGroundMarkerUpdate({
      ...supplementalData,
      photoId,
      photo: modifiedCloneOfPhoto,
      jobTraces: modifiedCloneOfTraces
    })
  };

  return jobUpdate;
}

/**
 * Composes an update object for a trace.
 * @param {string} traceId - The id of the trace to update
 * @param {Record<string, any>|null} traceDataMutations - The trace data mutations to apply
 * @param {object} supplementalData - Supplemental data
 * @param {object} supplementalData.jobTraces - The job's traces
 * @param {object} supplementalData.jobPhotos - The job's photos
 * @param {object} supplementalData.jobNodes - The job's nodes
 * @param {object} supplementalData.jobConnections - The job's connections
 * @param {object} supplementalData.jobStyles - The job's styles
 * @param {object} supplementalData.model - The job's associated model
 * @param {string} [supplementalData.userUid] - The uid of the user that is updating the trace (should be included if possible)
 * @returns {object} The update object (based at the job)
 */
export function composeTraceDataUpdate(traceId, traceDataMutations, supplementalData) {
  // Extract the supplemental data properties we need at this scope (others are passed down further)
  const { jobTraces, jobPhotos, userUid } = supplementalData;

  // Get the existing trace data and trace items
  const existingTraceData = jobTraces.trace_data?.[traceId] ?? {};
  const existingTraceItems = jobTraces.trace_items?.[traceId] ?? {};
  // Get the marker records associated with the trace
  const traceAssociatedMarkerRecords = getTraceAssociatedMarkerRecords(existingTraceItems, supplementalData);

  // Determine if this is either one of (1) a trace removal, or (2) a change from proposed false to true
  const isTraceRemoval = traceDataMutations == null;
  const changingProposedToTrue = traceDataMutations?.['proposed'] === true && existingTraceData.proposed !== true;

  // Clone the traces and photos to avoid mutating the original, and apply the trace data mutations
  const modifiedCloneOfJobTraces = structuredClone(jobTraces);
  const modifiedCloneOfJobPhotos = structuredClone(jobPhotos);
  Path.set(modifiedCloneOfJobTraces, `trace_data.${traceId}`, isTraceRemoval ? null : { ...existingTraceData, ...traceDataMutations });

  // Trace removals require removing the trace items and _trace from all associated markers
  if (isTraceRemoval) {
    Path.set(modifiedCloneOfJobTraces, `trace_items.${traceId}`, null);
    traceAssociatedMarkerRecords.forEach(({ markerPath, photoId }) => {
      Path.set(modifiedCloneOfJobPhotos, `${photoId}.photofirst_data.${markerPath}._trace`, null);
    });
  }
  // Changing proposed to true requires removing mr_move from all associated markers
  if (changingProposedToTrue) {
    traceAssociatedMarkerRecords.forEach(({ markerPath, photoId }) => {
      Path.set(modifiedCloneOfJobPhotos, `${photoId}.photofirst_data.${markerPath}.mr_move`, null);
    });
  }

  // Get the trace-associated items (using modified photos) and determine if any MR state changes are necessary (using modified traces)
  const traceAssociatedItemRecords = getTraceAssociatedItemRecords(existingTraceItems, {
    ...supplementalData,
    jobPhotos: modifiedCloneOfJobPhotos
  });
  const itemMRStateUpdates = traceAssociatedItemRecords.reduce((update, traceAssociatedItemRecord) => {
    const itemMRStateUpdate = composeMRStateAndAutoGroundMarkerUpdate({
      ...supplementalData,
      ...traceAssociatedItemRecord,
      jobTraces: modifiedCloneOfJobTraces
    });
    return { ...update, ...itemMRStateUpdate };
  }, {});

  // Compose the update object
  const jobUpdate = {
    ...(isTraceRemoval
      ? // If we are removing the trace, we need to remove the trace data, trace items, and _trace from all associated markers
        {
          [`traces/trace_data/${traceId}`]: null,
          [`traces/trace_items/${traceId}`]: null,
          ...Object.fromEntries(
            // TODO (2024-09-19): Technically, we should be using composePhotoElementUpdate here, but in its current form, it is a bit
            // wasteful (we don't need to call DataViews to get all markers here, we already have the paths); additionally, that function
            // will ALSO generate updates for the MR state and ground marker, which we are already doing here. We might be able to refactor
            // that function to be more efficient and then use it here, or do something like what we did with item delete composers in the
            // the other composer file (pass down information about what updates are already handled so we don't need to do them again).
            traceAssociatedMarkerRecords.flatMap(({ markerPath, photoId }) => [
              [`photos/${photoId}/photofirst_data/${markerPath}/_trace`, null],
              [`photos/${photoId}/photofirst_data/_editors/${userUid}`, serverTimestamp()]
            ])
          )
        }
      : // Otherwise, we just need to update the trace data and trace items
        Object.fromEntries(
          Object.entries(traceDataMutations).map(([traceDataProperty, traceDataValue]) => [
            `traces/trace_data/${traceId}/${traceDataProperty}`,
            traceDataValue
          ])
        )),
    // Update the MR state (and ground marker if necessary) for all items connected to the trace
    ...itemMRStateUpdates,
    // If we are changing the proposed property, we need to remove mr_move from all associated markers
    ...(changingProposedToTrue && {
      ...Object.fromEntries(
        traceAssociatedMarkerRecords.flatMap(({ markerPath, photoId }) => [
          [`photos/${photoId}/photofirst_data/${markerPath}/mr_move`, null],
          [`photos/${photoId}/photofirst_data/_editors/${userUid}`, serverTimestamp()]
        ])
      )
    })
  };

  return jobUpdate;
}

// Composer helper functions

/**
 * Get a copy of element data shaped for nesting, matching what the photo viewer writes when a marker is dragged onto a parent.
 * @param {object|boolean} elementData
 * @returns {object} The element data with its own height data removed, holding _exists only when nothing else is left.
 */
function getNestedElementData(elementData) {
  const nestedElementData = { ...elementData };
  // Nested elements take their height from their top-level parent
  for (const heightKey of ['_measured_height', '_manual_height', 'manual_height', 'pixel_selection']) delete nestedElementData[heightKey];
  if (Object.keys(nestedElementData).length === 0) nestedElementData._exists = true;
  return nestedElementData;
}

/**
 * Delete the _exists placeholder from an element and from its parent wherever that object now holds other data.
 * @param {object} photo The photo to edit in place.
 * @param {string} elementPath Dot-delimited path of the element under photofirst_data.
 */
function removeRedundantPlaceholders(photo, elementPath) {
  for (const path of [elementPath, getParentElementPath(elementPath)]) {
    if (path == null) continue;
    const elementData = Path.get(photo, `photofirst_data.${path}`);
    if (hasOwnElementData(elementData)) delete elementData._exists;
  }
}

/**
 * Mark an element as existing, as true for a top-level element or with _exists for a nested one, matching the photo viewer.
 * @param {object} photo The photo to edit in place.
 * @param {string} elementPath Dot-delimited path of the element under photofirst_data.
 */
function setElementPlaceholder(photo, elementPath) {
  const isNested = getParentElementPath(elementPath) != null;
  if (isNested) Path.set(photo, `photofirst_data.${elementPath}._exists`, true);
  else Path.set(photo, `photofirst_data.${elementPath}`, true);
}

/**
 * Check whether element data holds anything besides its _exists placeholder.
 * @param {object|boolean} elementData
 * @returns {boolean} True when the element would keep stored data without a placeholder.
 */
function hasOwnElementData(elementData) {
  if (typeof elementData !== 'object') return false;
  return Object.entries(elementData ?? {}).some(([key, value]) => key !== '_exists' && hasStoredData(value));
}

/**
 * Set the placeholder on the parent of a removed element when nothing else is left on it, so the database keeps the parent.
 * @param {object} photo The photo to edit in place.
 * @param {string} elementPath Dot-delimited path of the removed element under photofirst_data.
 */
function addPlaceholderToEmptiedParent(photo, elementPath) {
  const parentElementPath = getParentElementPath(elementPath);
  if (parentElementPath == null) return;

  const parentElementData = Path.get(photo, `photofirst_data.${parentElementPath}`);
  if (!hasStoredData(parentElementData)) setElementPlaceholder(photo, parentElementPath);
}

/**
 * Get the path of the element that an element is nested under.
 * @param {string} elementPath Dot-delimited path of the element under photofirst_data.
 * @returns {string|null} The parent element path, or null for a top-level element.
 */
function getParentElementPath(elementPath) {
  const childrenIndex = elementPath.lastIndexOf('._children.');
  return childrenIndex === -1 ? null : elementPath.slice(0, childrenIndex);
}

/**
 * Check whether a value would keep anything in the database, since null leaves and objects holding only nulls are dropped.
 * @param {any} value
 * @returns {boolean} True when the value holds at least one non-null leaf.
 */
function hasStoredData(value) {
  if (value == null) return false;
  if (typeof value !== 'object') return true;
  return Object.values(value).some((childValue) => hasStoredData(childValue));
}

/**
 * Checks the element data against the model to ensure point elements have pixel selections and chip elements do not.
 * @param {string} elementType - The type of the element
 * @param {PhotoElementCreateParams|PhotoElementUpdateParams} otherNewElementData - Other data being set on the element
 * @param {PhotoElementSupplementalData} supplementalData - Supplemental data
 * @param {boolean} [isNested=false] Whether the element sits under a parent element.
 */
function checkPhotoElementIsValid(elementType, otherNewElementData, supplementalData, isNested = false) {
  const photoElementInputType = supplementalData?.model?.input_models?.[elementType]?.element_type;
  const hasPixelSelection = otherNewElementData?.pixel_selection != null;
  const hasManualHeight = otherNewElementData?.manual_height != null;

  const isNotValidChipElement = photoElementInputType === 'chip' && (hasPixelSelection || hasManualHeight);
  // Nested elements take their height from their top-level parent, so only top-level point elements need a pixel selection
  const isNotValidPointElement = photoElementInputType === 'point' && !isNested && !hasPixelSelection;

  if (isNotValidChipElement) {
    throw new PhotoElementValidationError(
      `Element type "${elementType}" is a chip marker and cannot have a pixel selection or manual height`
    );
  }

  if (isNotValidPointElement) {
    throw new PhotoElementValidationError(`Element type "${elementType}" is a point marker and requires a pixel selection`);
  }
}

/**
 * Checks the model lists an element type in the allowed children of its new parent, matching the photo viewer drop rule.
 * @param {string} elementType
 * @param {string|null|undefined} parentIdOrNull Id of the new parent, or null or undefined when the element is not being nested.
 * @param {Record<string, object>} allElements
 * @param {PhotoElementSupplementalData} supplementalData
 */
function checkPhotoElementNestingIsValid(elementType, parentIdOrNull, allElements, supplementalData) {
  if (parentIdOrNull == null) return;
  // A missing parent is reported by getNewElementPath, so it is skipped here
  const parentElement = Object.hasOwn(allElements, parentIdOrNull) ? allElements[parentIdOrNull] : null;
  if (parentElement == null) return;

  const parentElementType = parentElement._context.type;
  const parentInputModel = supplementalData?.model?.input_models?.[parentElementType];
  // Parent types missing from the model are left unchecked, like the other model rules in this file
  if (parentInputModel == null) return;

  const allowedChildren = Object.values(parentInputModel.allowed_children ?? {});
  if (!allowedChildren.includes(elementType)) {
    throw new PhotoElementValidationError(`Element type "${elementType}" is not an allowed child of element type "${parentElementType}"`);
  }
}

/**
 * Get the top-level marker entries that should have measured heights (filtering out markers whose input model definitions do not have
 * measured heights or whose pixel selections are null).
 * @param {object} markersByInputModel - The markers organized by input model (from photofirst_data)
 * @param {object} inputModelDefinitions - The input model definitions
 * @returns {[string, object][]} The top-level markers paired with a combined input-model-and-marker key
 */
function getMeasuredTopLevelMarkerEntries(markersByInputModel, inputModelDefinitions) {
  const inputModelsWithMeasuredHeightsEntries = Object.entries(markersByInputModel).filter(
    ([inputModelKey]) => inputModelDefinitions?.[inputModelKey]?._attributes?.pixel_selection?._measured_height != null
  );
  const topLevelMarkerEntries = inputModelsWithMeasuredHeightsEntries.flatMap(([inputModelKey, topLevelMarkers]) =>
    Object.entries(topLevelMarkers).map(
      ([topLevelMarkerKey, topLevelMarker]) => /** @type {[string, object]} */ ([`${inputModelKey}/${topLevelMarkerKey}`, topLevelMarker])
    )
  );
  return topLevelMarkerEntries.filter(([, inputModelInstance]) => inputModelInstance.pixel_selection != null);
}

/**
 * @typedef {object} TraceAssociatedItemRecord
 * @property {string} itemType - The type of the item
 * @property {string} itemId - The id of the item
 * @property {object} item - The item
 * @property {string} photoId - The id of the photo
 * @property {object} photo - The photo
 */

/**
 * Get the trace-associated photos and items for a set of trace items.
 * @param {object} traceItems - The trace items
 * @param {object} supplementalData - Supplemental data
 * @param {object} supplementalData.jobPhotos - The job's photos
 * @param {object} supplementalData.jobNodes - The job's nodes
 * @param {object} supplementalData.jobConnections - The job's connections
 * @returns {TraceAssociatedItemRecord[]} The trace-associated photos and items
 */
function getTraceAssociatedItemRecords(traceItems, supplementalData) {
  const { jobPhotos, jobNodes, jobConnections } = supplementalData;
  const unfilteredTraceAssociatedItems = Object.keys(traceItems).map((photoId) => {
    // Get the photo data and the first associated item
    const photo = jobPhotos[photoId];
    const itemRecord = getPhotoAssociatedItemRecord(photo, jobNodes, jobConnections);

    // If the associated item does not exist, return null
    if (itemRecord == null) return;

    // Return all of the data we'll need to check for MR state changes
    return { photoId, photo, ...itemRecord };
  });

  // Filter out any null items
  return unfilteredTraceAssociatedItems.filter((itemData) => itemData != null);
}

/**
 * @typedef {object} TraceAssociatedMarkerRecord
 * @property {string} markerPath - The dot-delimited path to the marker
 * @property {object} marker - The marker
 * @property {object} photo - The photo
 * @property {string} photoId - The id of the photo
 */

/**
 * Get the trace-associated markers for a set of trace items.
 * @param {object} traceItems - The trace items
 * @param {object} supplementalData - Supplemental data
 * @param {object} supplementalData.jobPhotos - The job's photos
 * @returns {TraceAssociatedMarkerRecord[]} The trace-associated markers
 */
function getTraceAssociatedMarkerRecords(traceItems, supplementalData) {
  const { jobPhotos } = supplementalData;
  const traceAssociatedMarkers = Object.entries(traceItems).flatMap(([photoId, markerAddressesByInputModel]) => {
    // Get the photo data and the first associated item
    const photo = jobPhotos[photoId];
    // If there is no associated photo, don't return any markers
    if (photo == null) return [];

    // Convert the marker addresses for each input model into dot-delimited paths
    const dotDelimitedMarkerPaths = Object.entries(markerAddressesByInputModel).flatMap(([inputModelKey, markerAddresses]) =>
      Object.keys(markerAddresses).map((markerAddress) => `${inputModelKey}.${markerAddress.replace(/\:/g, '.')}`)
    );

    // Return all of the markers that are at the specified paths
    return dotDelimitedMarkerPaths.map((markerPath) => {
      const marker = Path.get(photo, `photofirst_data.${markerPath}`);
      return { markerPath, marker, photo, photoId };
    });
  });

  return traceAssociatedMarkers;
}

/**
 * @param {object} photofirstData - The photofirst data object
 * @param {?PixelSelection} pixelSelection - The pixel selection point
 */
function getMeasuredHeight(photofirstData, pixelSelection) {
  const stickAlignData = photofirstData?.stick_align;
  if (stickAlignData == null || pixelSelection == null) return null;

  const [{ percentX, percentY }] = pixelSelection;
  const percentWidth = percentX * stickAlignData.width;
  const percentHeight = percentY * stickAlignData.height;
  return calibratePhoto.getInches(percentWidth / 100, percentHeight / 100, stickAlignData);
}

/**
 * Walks a photo element and its children, setting or removing trace items according to data already set on the element.
 * @param {string} photoId - Id of photo
 * @param {string} elementPath - Dot-delimited path to the photo element
 * @param {object} elementData - Photo element data
 * @param {object} traces - Traces object from the job
 * @param {object} [options]
 * @param {boolean} [options.remove] - Whether to remove the trace items (useful for if the element is being moved or deleted)
 */
function setElementAndChildrenTraceItems(photoId, elementPath, elementData, traces, options) {
  const { remove = false } = options ?? {};
  const elementTraceId = elementData._trace;
  const elementChildren = elementData._children ?? {};

  // If the current element has a trace id, set (or remove) the trace item
  if (elementTraceId) {
    const valueToSet = remove ? null : true;
    const elementPathParts = elementPath.split('.');
    const [rootElementType, ...restOfPath] = elementPathParts;

    Path.set(traces, `trace_items.${elementTraceId}.${photoId}.${rootElementType}.${restOfPath.join(':')}`, valueToSet);
  }

  // Loop through the children of the current element and recursively call this function
  for (const childType in elementChildren) {
    for (const childKey in elementChildren[childType]) {
      const childElement = elementChildren[childType][childKey];
      const childElementPath = `${elementPath}._children.${childType}.${childKey}`;

      setElementAndChildrenTraceItems(photoId, childElementPath, childElement, traces, options);
    }
  }
}

/**
 * Get the new path for a photo element when it is moved to a new parent.
 * @param {string|null} parentIdOrNull - The id of the new parent element, or null if the element is being moved to the root
 * @param {object} elementBeingMoved - The photo element being moved
 * @param {Record<string, object>} allElements - The list of all elements in the photo
 * @returns {string} The new path for the photo element
 */
function getNewElementPath(parentIdOrNull, elementBeingMoved, allElements) {
  const { type: elementType, id: elementId } = elementBeingMoved._context;
  if (parentIdOrNull === null) return `${elementType}.${elementId}`;

  const newParentElement = Object.hasOwn(allElements, parentIdOrNull) ? allElements[parentIdOrNull] : null;
  if (newParentElement == null) throw new Error(`Parent element with id ${parentIdOrNull} does not exist`);

  const newParentElementPath = newParentElement._context.path.split('/').join('.');
  return `${newParentElementPath}._children.${elementType}.${elementId}`;
}

/**
 * Get a flat list of all photo elements.
 *
 * Reminiscent of `DataViews.help.getMarkers` but it returns everything by default, including chip elements.
 * @param {object} [photoOrElementChildren] - Photo or element children object
 * @param {object} [list] - List to populate with the elements
 * @param {string[]} [pathParts] - Path parts to use for the element paths
 * @returns {Record<string, object>} Flat list of photo elements
 */
export function getFlatPhotoElementList(photoOrElementChildren = {}, list = {}, pathParts = []) {
  // Loop through the elements and their children
  for (const [elementType, elements] of Object.entries(photoOrElementChildren)) {
    // Skip element types that start with an underscore (e.g. _editors)
    if (elementType.startsWith('_')) continue;
    // Skip stick align and anchor calibration elements
    if (elementType === 'stick_align' || elementType === 'anchor_calibration') continue;

    for (const [elementId, elementData] of Object.entries(elements)) {
      // Add the element to the list
      const elementPathParts = [...pathParts, elementType, elementId];
      const context = { id: elementId, path: elementPathParts.join('/'), type: elementType };
      list[elementId] = { ...elementData, _context: context };

      // Recursively call this function for the children of the current element
      if (elementData._children) getFlatPhotoElementList(elementData._children, list, [...elementPathParts, '_children']);
    }
  }

  return list;
}

// Typedefs

/**
 * @typedef {object} PhotoElementSupplementalData
 * @property {object} supplementalData.photo - Photo object
 * @property {object} supplementalData.model - Job's associated model
 * @property {string} supplementalData.itemType - Type of the item that the photo belongs to
 * @property {object} supplementalData.item - Item that the photo belongs to
 * @property {string} supplementalData.itemId - Id of the item that the photo belongs to
 * @property {object} supplementalData.jobTraces - Job's traces
 * @property {object} supplementalData.jobNodes - Job's nodes
 * @property {object} supplementalData.jobConnections - Job's connections
 * @property {object} supplementalData.jobStyles - Job's styles
 * @property {string} [supplementalData.userUid] - Uid of the user that is updating the marker (should be included if possible)
 */
